import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../provider/authContextType';
import { Liferay } from '../liferay/liferay';

interface ProfileData {
  value: any;
}

interface QueryParams {
  identityDocument: string;
  identityDocumentTypeId: number;
}

const fetchProfile = async ({
  identityDocument,
  identityDocumentTypeId,
  origin,
}: QueryParams & { origin: string | null }) => {
  try {
    const response = await axios.get(`${origin}/affiliation/bydocument`, {
      params: {
        IdentityDocument: identityDocument,
        IdentityDocumentTypeId: identityDocumentTypeId,
      },
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer `,
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Error al obtener el perfil'
    );
  }
};

const getAffiliationByDocument = async ({
  identityDocument,
  identityDocumentTypeId,
  origin,
}: QueryParams & { origin: string | null }) => {
  try {
    const response = await axios.get(
      `${origin}/affiliation/getAffiliationByDocument`,
      {
        params: {
          IdentityDocument: identityDocument,
          IdentityDocumentTypeId: identityDocumentTypeId,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Error al obtener el perfil'
    );
  }
};

const fetchPdfMedicCost = async (
  identityDocument: string,
  affiliate: string
) => {
  const response = await fetch(
    `http://10.254.12.52:8007/api/Siteds/GeneratePdfMedicCost?IdentityDocument=${identityDocument}&Affiliate=${affiliate}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer `,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch PDF medic cost');
  }

  return response.json();
};

const fetchPdfAffiliateConstancy = async (
  memberCode: string,
  planId: string,
  familyNumber: string
) => {
  const response = await fetch(
    `http://10.254.12.52:8007/api/Affiliation/GetPdfAffiliateConstancyById?memberCode=${memberCode}&planId=${planId}&familyNumber=${familyNumber}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch PDF affiliate constancy');
  }

  return response.json();
};

const fetchFamily = async ({
  identityDocument,
  identityDocumentTypeId,
  origin,
}: QueryParams & { origin: string | null }) => {
  const response = await axios.get(
    `${origin}/affiliation/getFamilyActiveByIdentityDocument`,
    {
      params: {
        IdentityDocument: identityDocument,
        IdentityDocumentTypeId: identityDocumentTypeId,
      },
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer`,
      },
    }
  );
  return response.data;
};

const versionateMember = async (data: any, origin: string | null) => {
  const response = await axios.post(
    `${origin}/affiliation/versionateMember`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer`,
      },
    }
  );
  return response.data;
};

export const useFetchProfile = (
  identityDocument: string,
  identityDocumentTypeId: number
) => {
  const { origin } = useAuth();
  console.log('origin', origin);

  return useQuery({
    queryKey: ['profile', identityDocument, identityDocumentTypeId],
    queryFn: () => {
      return fetchProfile({ identityDocument, identityDocumentTypeId, origin });
    },
    enabled: !!identityDocument && !!identityDocumentTypeId,
  });
};

export const useGetAffiliationByDocument = (
  identityDocument: string,
  identityDocumentTypeId: number
) => {
  return useQuery({
    queryKey: [
      'getAffiliationByDocument',
      identityDocument,
      identityDocumentTypeId,
    ],
    queryFn: () => {
      return getAffiliationByDocument({
        identityDocument,
        identityDocumentTypeId,
        origin,
      });
    },
    enabled: !!identityDocument && !!identityDocumentTypeId,
  });
};

export const useFetchFamily = (
  identityDocument: string,
  identityDocumentTypeId: number
) => {
  return useQuery({
    queryKey: ['family', identityDocument, identityDocumentTypeId],
    queryFn: () => {
      return fetchFamily({ identityDocument, identityDocumentTypeId, origin });
    },
    enabled: !!identityDocument && !!identityDocumentTypeId,
  });
};

export const useFetchFamilyMutate = () => {
  return useMutation({
    mutationFn: (data: QueryParams) => {
      const { identityDocument, identityDocumentTypeId } = data;
      return fetchFamily({ identityDocument, identityDocumentTypeId, origin });
    },
  });
};

export const useFetchPdfMedicCost = () => {
  return useMutation({
    mutationFn: ({
      identityDocument,
      affiliate,
    }: {
      identityDocument: string;
      affiliate: string;
    }) => {
      return fetchPdfMedicCost(identityDocument, affiliate);
    },
  });
};

export const useProfileData = () => {
  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData<ProfileData>(['profileCache']);
  if (cachedData) {
    return {
      data: cachedData,
      isLoading: false,
      error: null,
      reset: () => queryClient.removeQueries({ queryKey: ['profileCache'] }),
    };
  }
  return { data: null, isLoading: false, error: 'No data in cache' };
};

export const useFetchProfileMutate = () => {
  const queryClient = useQueryClient();
  const { origin } = useAuth();
  return useMutation({
    mutationFn: (data: QueryParams) => {
      const { identityDocument, identityDocumentTypeId } = data;
      return fetchProfile({ identityDocument, identityDocumentTypeId, origin });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['profileCache'], data);
    },
  });
};

export const useFetchPdfAffiliateConstancy = () => {
  return useMutation({
    mutationFn: ({
      memberCode,
      planId,
      familyNumber,
    }: {
      memberCode: string;
      planId: string;
      familyNumber: string;
    }) => {
      return fetchPdfAffiliateConstancy(memberCode, planId, familyNumber);
    },
  });
};

export const useVersionateMember = () => {
  const { origin } = useAuth();
  return useMutation({
    mutationFn: (data: any) => {
      return versionateMember(data, origin);
    },
  });
};
